/**
 * コンビニ決済コンポーネント
 * GMO Payment Gateway経由でのコンビニ払い
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
} from '@mui/material';
import {
  Store as StoreIcon,
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  QrCode as QrCodeIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// コンビニアイコンとカラー
const CONVENIENCE_STORES = {
  '00007': { name: 'セブンイレブン', color: '#00A040', icon: '7' },
  '10001': { name: 'ローソン', color: '#0068B7', icon: 'L' },
  '10002': { name: 'ファミリーマート', color: '#00A0E9', icon: 'F' },
  '10005': { name: 'ミニストップ', color: '#E4007F', icon: 'M' },
  '10008': { name: 'デイリーヤマザキ', color: '#FF6600', icon: 'D' },
  '10009': { name: 'セイコーマート', color: '#EE1C25', icon: 'S' },
};

const ConveniencePayment = ({
  amount,
  orderId,
  storeId,
  onSuccess,
  onError,
  onCancel,
}) => {
  const [step, setStep] = useState('select'); // select, form, complete
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    customerName: '',
    customerKana: '',
    phoneNumber: '',
    mailAddress: '',
  });
  // フィールドごとのエラー状態（インラインバリデーション用）
  const [fieldErrors, setFieldErrors] = useState({
    customerName: '',
    phoneNumber: '',
    mailAddress: '',
  });
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // コンビニ選択
  const handleSelectStore = (code) => {
    setSelectedStore(code);
    setStep('form');
    setError(null);
  };

  // フォーム入力
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // 入力時にエラーをクリア
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // フィールドのブラー時バリデーション
  const handleFieldBlur = (fieldName) => {
    const value = formData[fieldName];
    let errorMsg = '';

    switch (fieldName) {
      case 'customerName':
        if (!value.trim()) {
          errorMsg = 'お名前を入力してください';
        }
        break;
      case 'phoneNumber':
        if (!value.trim()) {
          errorMsg = '電話番号を入力してください';
        } else {
          const phone = formatPhoneNumber(value);
          if (phone.length < 10 || phone.length > 11) {
            errorMsg = '電話番号の形式が正しくありません';
          }
        }
        break;
      case 'mailAddress':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errorMsg = 'メールアドレスの形式が正しくありません';
        }
        break;
      default:
        break;
    }

    setFieldErrors(prev => ({ ...prev, [fieldName]: errorMsg }));
  };

  // 電話番号のフォーマット（ハイフンなし）
  const formatPhoneNumber = (phone) => {
    return phone.replace(/[^0-9]/g, '');
  };

  // バリデーション
  const validateForm = () => {
    if (!formData.customerName.trim()) {
      setError('お名前を入力してください');
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      setError('電話番号を入力してください');
      return false;
    }
    const phone = formatPhoneNumber(formData.phoneNumber);
    if (phone.length < 10 || phone.length > 11) {
      setError('電話番号の形式が正しくありません');
      return false;
    }
    return true;
  };

  // 決済作成
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/payments/convenience/create', {
        orderId,
        convenienceCode: selectedStore,
        customerName: formData.customerName,
        customerKana: formData.customerKana || formData.customerName,
        phoneNumber: formatPhoneNumber(formData.phoneNumber),
        mailAddress: formData.mailAddress || undefined,
        storeId,
      });

      if (response.data.success) {
        setResult(response.data);
        setStep('complete');
        onSuccess?.(response.data);
      } else {
        throw new Error(response.data.error || '決済の作成に失敗しました');
      }
    } catch (err) {
      console.error('コンビニ決済エラー:', err);
      setError(err.response?.data?.error || err.message || '決済の作成に失敗しました');
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  // 払込番号コピー
  const handleCopyReceiptNumber = async () => {
    if (result?.receiptNumber) {
      try {
        await navigator.clipboard.writeText(result.receiptNumber);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('コピー失敗:', err);
      }
    }
  };

  // コンビニ選択画面
  if (step === 'select') {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          コンビニを選択
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          お支払いするコンビニを選んでください
        </Typography>

        <Grid container spacing={2}>
          {Object.entries(CONVENIENCE_STORES).map(([code, store]) => (
            <Grid item xs={6} key={code}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: 2,
                  borderColor: 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: store.color,
                    transform: 'scale(1.02)',
                  },
                }}
                onClick={() => handleSelectStore(code)}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: store.color,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 24,
                      fontWeight: 'bold',
                      mx: 'auto',
                      mb: 1,
                    }}
                  >
                    {store.icon}
                  </Box>
                  <Typography variant="body2" fontWeight="medium">
                    {store.name}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" onClick={onCancel}>
            キャンセル
          </Button>
        </Box>
      </Box>
    );
  }

  // フォーム入力画面
  if (step === 'form') {
    const store = CONVENIENCE_STORES[selectedStore];

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            size="small"
            onClick={() => setStep('select')}
            sx={{ mr: 2 }}
          >
            ← 戻る
          </Button>
          <Chip
            label={store.name}
            sx={{ bgcolor: store.color, color: 'white' }}
          />
        </Box>

        <Typography variant="h6" gutterBottom>
          お客様情報を入力
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" sx={{ mt: 2 }}>
          <TextField
            fullWidth
            required
            label="お名前"
            name="customerName"
            value={formData.customerName}
            onChange={handleInputChange}
            onBlur={() => handleFieldBlur('customerName')}
            error={!!fieldErrors.customerName}
            helperText={fieldErrors.customerName}
            sx={{ mb: 2 }}
            placeholder="山田太郎"
          />
          <TextField
            fullWidth
            label="フリガナ"
            name="customerKana"
            value={formData.customerKana}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
            placeholder="ヤマダタロウ"
          />
          <TextField
            fullWidth
            required
            label="電話番号"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            onBlur={() => handleFieldBlur('phoneNumber')}
            error={!!fieldErrors.phoneNumber}
            helperText={fieldErrors.phoneNumber}
            sx={{ mb: 2 }}
            placeholder="090-1234-5678"
            inputProps={{ inputMode: 'tel' }}
          />
          <TextField
            fullWidth
            label="メールアドレス（任意）"
            name="mailAddress"
            type="email"
            value={formData.mailAddress}
            onChange={handleInputChange}
            onBlur={() => handleFieldBlur('mailAddress')}
            error={!!fieldErrors.mailAddress}
            helperText={fieldErrors.mailAddress || "払込票情報をメールで受け取れます"}
            sx={{ mb: 2 }}
            placeholder="example@email.com"
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body1">お支払い金額</Typography>
            <Typography variant="h5" fontWeight="bold">
              ¥{amount?.toLocaleString()}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
              sx={{ flex: 1 }}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <StoreIcon />}
              sx={{ flex: 2, bgcolor: store.color }}
            >
              {loading ? '処理中...' : '払込票を発行'}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // 完了画面
  if (step === 'complete' && result) {
    const store = CONVENIENCE_STORES[selectedStore];

    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          払込票を発行しました
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          以下の番号を{store.name}でお伝えください
        </Typography>

        <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              払込票番号
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Typography variant="h4" fontWeight="bold" sx={{ letterSpacing: 2 }}>
                {result.receiptNumber}
              </Typography>
              <Button
                size="small"
                startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                onClick={handleCopyReceiptNumber}
              >
                {copied ? 'コピー済み' : 'コピー'}
              </Button>
            </Box>

            {result.confirmNumber && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  確認番号
                </Typography>
                <Typography variant="h6">
                  {result.confirmNumber}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                お支払い期限
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {result.paymentDeadline
                  ? new Date(result.paymentDeadline).toLocaleDateString('ja-JP')
                  : '7日以内'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                金額
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                ¥{result.amount?.toLocaleString()}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {result.paymentUrl && (
          <Button
            variant="outlined"
            startIcon={<QrCodeIcon />}
            href={result.paymentUrl}
            target="_blank"
            sx={{ mb: 2 }}
          >
            支払いページを開く
          </Button>
        )}

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          ※ 期限内にお支払いがない場合、自動的にキャンセルされます
        </Typography>

        <Button
          variant="contained"
          onClick={onCancel}
          fullWidth
        >
          閉じる
        </Button>
      </Box>
    );
  }

  return null;
};

export default ConveniencePayment;
